import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, Image as ImageIcon, MapPin } from "lucide-react";
import { adminClient } from "@/lib/apiClient";
import { categoryLabel } from "@/data/portfolioCategories";
import AdminProjectModal from "@/components/admin/AdminProjectModal";

function StatusPill({ published }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${
        published ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"
      }`}
    >
      {published ? <Eye size={11} /> : <EyeOff size={11} />} {published ? "Live" : "Hidden"}
    </span>
  );
}

export default function AdminPortfolio() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState({ open: false, mode: "create", initial: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminClient.get("/admin/projects");
      setProjects(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load projects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = () => setModal({ open: true, mode: "create", initial: null });
  const handleEdit = (project) => setModal({ open: true, mode: "edit", initial: project });
  const closeModal = () => setModal({ open: false, mode: "create", initial: null });

  const handleSave = async (payload) => {
    if (modal.mode === "edit" && modal.initial?.id) {
      await adminClient.patch(`/admin/projects/${modal.initial.id}`, payload);
    } else {
      await adminClient.post("/admin/projects", payload);
    }
    closeModal();
    await load();
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Delete "${project.title}"? This cannot be undone.`)) return;
    try {
      await adminClient.delete(`/admin/projects/${project.id}`);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Delete failed.");
    }
  };

  const togglePublished = async (project) => {
    try {
      await adminClient.patch(`/admin/projects/${project.id}`, {
        is_published: !project.is_published,
      });
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Could not update visibility.");
    }
  };

  return (
    <div data-testid="admin-portfolio">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="br-eyebrow">Portfolio Manager</div>
          <h2 className="font-display text-2xl tracking-widest uppercase mt-1">Projects</h2>
          <p className="text-sm text-gray-600 mt-1">Create and manage projects shown on the public /portfolio page.</p>
        </div>
        <button type="button" onClick={handleCreate} className="br-btn-primary" data-testid="admin-portfolio-new">
          <Plus size={14} /> New Project
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4" data-testid="admin-portfolio-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500" data-testid="admin-portfolio-loading">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading projects…
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-8 text-center" data-testid="admin-portfolio-empty">
          <div className="w-12 h-12 mx-auto rounded-full bg-br-paper flex items-center justify-center text-br-bronze mb-3">
            <ImageIcon size={20} />
          </div>
          <div className="font-display tracking-widest uppercase text-lg">No projects yet</div>
          <p className="mt-1 text-sm text-gray-600">Click “New Project” to upload your first project and images.</p>
          <button type="button" onClick={handleCreate} className="br-btn-primary mt-4">
            <Plus size={14} /> New Project
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-br-paper border-b border-gray-200">
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-gray-600">
                  <th className="px-4 py-3 font-bold">Project</th>
                  <th className="px-4 py-3 font-bold">Category</th>
                  <th className="px-4 py-3 font-bold">Photos</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {projects.map((p) => (
                  <tr key={p.id} className="hover:bg-br-paper/30" data-testid={`admin-project-row-${p.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-start gap-3">
                        <div className="w-14 h-14 rounded bg-br-paper overflow-hidden shrink-0 flex items-center justify-center text-gray-400">
                          {p.images?.[0] ? (
                            <img src={p.images[0].data_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <ImageIcon size={18} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{p.title}</div>
                          {p.city && (
                            <div className="flex items-center gap-1 text-[11px] text-gray-500 mt-0.5">
                              <MapPin size={10} /> {p.city}
                            </div>
                          )}
                          {p.is_before_after && (
                            <div className="text-[10px] uppercase tracking-widest text-br-bronze font-bold mt-0.5">
                              Before / After
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">{categoryLabel(p.category)}</td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">{p.images?.length || 0}</td>
                    <td className="px-4 py-3 align-top">
                      <button
                        type="button"
                        onClick={() => togglePublished(p)}
                        className="cursor-pointer"
                        data-testid={`admin-project-toggle-${p.id}`}
                        aria-label={`Toggle published for ${p.title}`}
                      >
                        <StatusPill published={!!p.is_published} />
                      </button>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={() => handleEdit(p)} className="p-1.5 text-gray-500 hover:text-br-bronze" aria-label="Edit" data-testid={`admin-project-edit-${p.id}`}>
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(p)} className="p-1.5 text-red-500 hover:text-red-700" aria-label="Delete" data-testid={`admin-project-delete-${p.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AdminProjectModal
        open={modal.open}
        mode={modal.mode}
        initial={modal.initial}
        onClose={closeModal}
        onSave={handleSave}
      />
    </div>
  );
}
